import { CompOffRecord } from '../types/leavePolicy';
import { LeaveAuditService } from './leaveAuditService';
import { LeavePolicyService } from './leavePolicyService';
import { isResolvedIdentity, sameIdentity } from './identity';
import { generateClient } from 'aws-amplify/api';

const COMP_OFF_KEY = 'creyenteone:comp-off-records:v1';

let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const getAuthMode = () => ({ authMode: 'userPool' as const });
const getData = (result: any) => ('data' in result ? result.data : null);

const compOffFields = `
  id employeeEmail workedDate isHoliday daysGranted status expiryDate grantedBy reason
  createdAt decidedBy decidedAt decisionReason
`;

const listCompOffsQuery = /* GraphQL */ `
  query ListCompOffs($limit: Int, $nextToken: String) {
    listCompOffs(limit: $limit, nextToken: $nextToken) {
      items { ${compOffFields} }
      nextToken
    }
  }
`;

const createCompOffMutation = /* GraphQL */ `
  mutation CreateCompOff($input: CreateCompOffInput!) {
    createCompOff(input: $input) { ${compOffFields} }
  }
`;

const updateCompOffMutation = /* GraphQL */ `
  mutation UpdateCompOff($input: UpdateCompOffInput!) {
    updateCompOff(input: $input) { ${compOffFields} }
  }
`;

const deleteCompOffMutation = /* GraphQL */ `
  mutation DeleteCompOff($id: ID!) {
    deleteCompOff(id: $id) { id }
  }
`;

const VALID_STATUSES = ['APPROVED', 'PENDING', 'REJECTED', 'EXPIRED'] as const;

/**
 * Normalise a row coming off the wire.
 *
 * DynamoDB has no notion of "absent" for the optional fields the resolver writes as "", and
 * status casing has to survive a round-trip, so both are settled in one place rather than at
 * every comparison site.
 */
const normalize = (raw: any): CompOffRecord => {
  const status = String(raw?.status || '').toUpperCase();
  return {
    id: String(raw?.id || ''),
    employeeEmail: String(raw?.employeeEmail || ''),
    workedDate: String(raw?.workedDate || ''),
    isHoliday: !!raw?.isHoliday,
    daysGranted: Number(raw?.daysGranted) || 0,
    status: (VALID_STATUSES as readonly string[]).includes(status)
      ? (status as CompOffRecord['status'])
      : 'PENDING',
    expiryDate: String(raw?.expiryDate || ''),
    grantedBy: String(raw?.grantedBy || ''),
    reason: String(raw?.reason || ''),
    createdAt: String(raw?.createdAt || ''),
    decidedBy: raw?.decidedBy || undefined,
    decidedAt: raw?.decidedAt || undefined,
    decisionReason: raw?.decisionReason || undefined,
  };
};

const notifyChanged = () => {
  window.dispatchEvent(new Event('leave-balance-updated'));
  window.dispatchEvent(new Event('storage'));
};

/**
 * Comp-Off: days worked on a weekend or holiday, claimed back as leave.
 *
 * **The store is the server**; localStorage is only a cache of it. That split exists because
 * the reads have to stay synchronous — leaveBalanceService.computeSnapshotsFor is documented
 * as pure and non-fetching, and leaveReportService reads records inline — while the records
 * themselves must be shared between the employee who claims, the manager who approves, and
 * HR who audits. So `refresh()` pulls the server into the cache and every read below is a
 * cache read.
 *
 * Previously there was no server at all: a claim was written to the applicant's own browser,
 * so the approver's queue could only ever be empty and no manager could ever act on one.
 * Mutations now go to AppSync first and fall back to a local write only when it is
 * unreachable, matching LeaveService.
 */
/**
 * Who a direct Comp-Off grant was made by, in terms of the authority used rather than the
 * person. A manager may only grant to their own reports; HR may grant to anyone.
 */
export type GrantCapacity = 'Manager' | 'HR';

export class CompOffService {
  /**
   * Today as a calendar date, in the viewer's own timezone.
   *
   * NOT `new Date().toISOString().slice(0, 10)`, which is the date in UTC. At UTC+5:30 that
   * still reads as yesterday until 05:30 local — so between local midnight and 05:30 a day
   * worked "today" counted as future-dated and therefore unusable, and a day that expired
   * yesterday was still counted in the balance. An off-by-one that appeared and cleared
   * itself every morning, which is the hardest kind to be told about.
   *
   * expiryDateFor deliberately keeps UTC internally: that arithmetic adds a fixed number of
   * days to a stored date, and UTC is what stops DST shifting the result. Which day it is
   * *now* is a different question, and the answer is local.
   */
  static todayIso(asOf: Date = new Date()): string {
    const y = asOf.getFullYear();
    const m = String(asOf.getMonth() + 1).padStart(2, '0');
    const d = String(asOf.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /**
   * The calendar date of a stored timestamp, in the viewer's own timezone.
   *
   * `.slice(0, 10)` on the stored ISO string would be the UTC date, and at UTC+5:30 everything
   * approved between local midnight and 05:30 would read as yesterday — precisely inverting the
   * same-day rule for the first five and a half hours of every day. Same reasoning as todayIso.
   */
  static localDateOf(timestamp?: string): string {
    if (!timestamp) return '';
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? '' : this.todayIso(parsed);
  }

  /**
   * When this Comp-Off day became spendable.
   *
   * `decidedAt` for a claim a manager or HR approved; `createdAt` for a row that was written
   * already APPROVED, which is what a direct grant is. Falling back matters: without it a direct
   * grant has no decision timestamp at all and would look like it had always been available.
   */
  static approvalDateOf(record: CompOffRecord): string {
    return this.localDateOf(record.decidedAt) || this.localDateOf(record.createdAt);
  }

  /**
   * Whether this day was approved today and therefore cannot be spent yet.
   *
   * The business rule: a Comp-Off approved on the 18th is not usable on the 18th, only from the
   * next eligible day. Deliberately keyed on the *approval* date and nothing else, so a day
   * approved last week stays usable no matter when it was worked — the rule withholds newly
   * granted credit, it does not re-open settled balances.
   *
   * A record with no resolvable approval timestamp is treated as *not* same-day: those are older
   * rows written before decidedAt existed, and withholding them would silently shrink balances
   * that have been correct for months.
   */
  static isApprovedToday(record: CompOffRecord, asOf: Date = new Date()): boolean {
    const approvedOn = this.approvalDateOf(record);
    return approvedOn !== '' && approvedOn === this.todayIso(asOf);
  }

  /**
   * Cache reads — synchronous by design, see the class note.
   *
   * Called with no argument this is every row in the table, which is what the approver queue,
   * Reports and the cache-maintenance helpers below need. Called *with* an employee it is that
   * employee's rows and nobody else's — including when the identity handed in turns out to be
   * empty, which is a failed lookup and not a request for everyone. That distinction used to
   * be `if (employeeEmail)`, and an empty string took the unscoped branch: a session whose
   * email Cognito never supplied was shown a Comp-Off balance summed from the whole company's
   * approved days. See isResolvedIdentity in identity.ts.
   */
  static getRecords(employeeEmail?: string): CompOffRecord[] {
    try {
      const raw = localStorage.getItem(COMP_OFF_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const records: CompOffRecord[] = parsed.map(normalize);
      if (employeeEmail === undefined) return records;
      if (!isResolvedIdentity(employeeEmail)) {
        // Loud, because every symptom of this is a plausible-looking wrong number rather than
        // an error: the caller believes it asked about somebody.
        console.warn(
          '[CompOff] getRecords called with an empty employee identity — returning no records. ' +
          'The signed-in account has no email, so its Comp-Off cannot be identified.'
        );
        return [];
      }
      // sameIdentity, not string equality: records written before the domain move
      // carry the old address while the session reports the new one.
      return records.filter(r => sameIdentity(r.employeeEmail, employeeEmail));
    } catch {
      return [];
    }
  }

  private static writeCache(records: CompOffRecord[]): void {
    localStorage.setItem(COMP_OFF_KEY, JSON.stringify(records));
  }

  /** Replace one row in the cache, inserting it when it is new. */
  private static cacheUpsert(record: CompOffRecord): void {
    const all = this.getRecords();
    const index = all.findIndex(r => r.id === record.id);
    if (index >= 0) all[index] = record;
    else all.unshift(record);
    this.writeCache(all);
  }

  /**
   * Pull every Comp-Off row from the server into the cache.
   *
   * Scans the whole table rather than just the caller's rows: an approver needs their
   * reports' claims and HR needs everyone's, and the balance engine reads per-employee
   * totals out of the same cache for whoever it is asked about.
   *
   * Returns false when the server could not be reached, so a caller can tell "nothing to
   * approve" apart from "could not load" instead of showing an empty queue either way.
   */
  static async refresh(): Promise<boolean> {
    try {
      const result = await getClient().graphql({
        query: listCompOffsQuery,
        variables: { limit: 500 },
        ...getAuthMode(),
      } as any);
      const items = getData(result)?.listCompOffs?.items;
      if (!Array.isArray(items)) return false;

      // Only announce a *change*. leaveBalanceService.getBalances refreshes Comp-Off, and
      // several pages recompute balances in response to 'leave-balance-updated' — so firing
      // unconditionally here would have those pages trigger the very refresh that re-fires
      // the event, spinning forever. Comparing before writing makes the cycle converge:
      // the first pass may notify, the next finds identical data and stops.
      const next = JSON.stringify(items.map(normalize));
      if (next !== localStorage.getItem(COMP_OFF_KEY)) {
        localStorage.setItem(COMP_OFF_KEY, next);
        notifyChanged();
      }
      return true;
    } catch (error) {
      console.warn('[CompOff] refresh failed, using cached records:', error);
      return false;
    }
  }

  /**
   * Approved, unexpired Comp-Off for the year — the number the balance actually uses.
   *
   * The expiry check is the point: before it, a day was earned and then counted forever within
   * its year, because the stored expiryDate was written and never read. An employee could work
   * a Saturday in January and still be shown the day in December, long past the window HR set.
   */
  static getTotalEarned(employeeEmail: string, year: number): number {
    return this.usableRecords(employeeEmail, year).reduce((acc, r) => acc + r.daysGranted, 0);
  }

  /**
   * The records behind getTotalEarned — approved, in-year, already worked, not yet lapsed.
   *
   * Extracted so the total and the expiry breakdown below cannot disagree about which days
   * count. Two copies of "approved and unexpired" would eventually drift, and the symptom
   * would be a balance that does not match the dates shown next to it.
   *
   * The window has a lower bound as well as an upper one. An employee claiming their own
   * day cannot pick a future date — the form caps the input at today and rejects it on
   * submit — but HR's direct grant has neither guard, so a day granted for a shift that has
   * not happened yet was credited the moment it was entered. That let HR pre-arrange a
   * comp-off for a known upcoming holiday and accidentally hand the employee a day they
   * could spend before working it.
   *
   * Excluding it here rather than blocking the entry keeps the pre-grant useful: the record
   * stays visible on the Comp-Off page as scheduled, and it starts counting by itself on
   * the date worked, with the expiry window running from that same date. No second action
   * by HR, and nothing to remember.
   */
  private static usableRecords(employeeEmail: string, year: number): CompOffRecord[] {
    const todayIso = this.todayIso();
    return this.getRecords(employeeEmail).filter(r => {
      const rYear = new Date(r.workedDate || r.createdAt).getFullYear();
      if (rYear !== year || r.status !== 'APPROVED') return false;
      // Usable from the worked day itself, matching the "through the expiry day" rule at
      // the other end. A record with no workedDate is not treated as future-dated.
      if ((r.workedDate || '') > todayIso) return false;
      /*
       * Approved today is not spendable today — see isApprovedToday.
       *
       * Applied here rather than in the Apply form because this is the one function every
       * consumer of a Comp-Off balance goes through: getTotalEarned, getExpiringDays, the
       * balance snapshots and therefore the leave-application eligibility check all inherit it
       * from this single filter. Putting it in the form would have left the balance card and the
       * validator disagreeing with the dropdown.
       */
      if (this.isApprovedToday(r)) return false;
      return !this.isExpired(r);
    });
  }

  /**
   * Days approved today, which the balance is deliberately withholding until tomorrow.
   *
   * Exposed so a screen can explain the gap rather than leaving an employee to wonder why an
   * approval they just watched happen did not change their balance. Same filters as
   * usableRecords except that this keeps exactly what that one drops.
   */
  static getPendingUntilTomorrow(employeeEmail: string, year: number): CompOffRecord[] {
    const todayIso = this.todayIso();
    return this.getRecords(employeeEmail).filter(r => {
      const rYear = new Date(r.workedDate || r.createdAt).getFullYear();
      if (rYear !== year || r.status !== 'APPROVED') return false;
      if ((r.workedDate || '') > todayIso) return false;
      if (this.isExpired(r)) return false;
      return this.isApprovedToday(r);
    });
  }

  /**
   * Usable days paired with the date each one lapses, soonest first.
   *
   * Comp-Off is the expiry an employee is most likely to actually hit: the window is 45 days
   * by default, far shorter than a leave year, and it runs from the date worked rather than
   * from any date they chose. The balance has always dropped these on expiry — this is what
   * lets a screen warn before that happens instead of leaving the employee to notice the
   * number fell. Dates come from expiryDateFor, so they follow HR's current window setting
   * rather than whatever was stored when the claim was made.
   */
  static getExpiringDays(employeeEmail: string, year: number): { days: number; expiresOn: string }[] {
    return this.usableRecords(employeeEmail, year)
      .map(r => ({ days: r.daysGranted, expiresOn: this.expiryDateFor(r.workedDate) }))
      .filter(e => e.expiresOn !== '' && e.days > 0)
      .sort((a, b) => a.expiresOn.localeCompare(b.expiresOn));
  }

  /**
   * Write a new row, server first.
   *
   * `status` is what separates the two entry points: an employee's claim omits it and the
   * resolver defaults to PENDING, while HR's direct grant asks for APPROVED. Since only
   * APPROVED counts in getTotalEarned, a claim cannot credit anything on its own.
   */
  private static async create(
    input: {
      employeeEmail: string;
      workedDate: string;
      isHoliday: boolean;
      daysGranted: number;
      reason: string;
      grantedBy: string;
      expiryDate: string;
      status?: CompOffRecord['status'];
      decidedBy?: string;
      decidedAt?: string;
    }
  ): Promise<CompOffRecord> {
    let created: CompOffRecord | null = null;
    try {
      const result = await getClient().graphql({
        query: createCompOffMutation,
        variables: { input },
        ...getAuthMode(),
      } as any);
      const raw = getData(result)?.createCompOff;
      if (raw) created = normalize(raw);
    } catch (error) {
      console.warn('[CompOff] createCompOff failed, writing locally instead:', error);
    }

    if (!created) {
      created = normalize({
        ...input,
        id: `co-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        status: input.status || 'PENDING',
        createdAt: new Date().toISOString(),
      });
    }

    this.cacheUpsert(created);
    notifyChanged();
    return created;
  }

  /**
   * Stamped onto every new record. Was year-end of the date worked, which was the wrong rule
   * *and* was never enforced — see expiryDateFor.
   */
  private static expiryFor(workedDate: string): string {
    return this.expiryDateFor(workedDate);
  }

  /**
   * The authority a direct grant was made under.
   *
   * Recorded rather than derived, because the two are not the same question. Deriving it means
   * looking the granter up in the directory and reading their role *now* — so an HR admin who
   * later moves to a manager role rewrites the history of every grant they ever made, and a
   * manager promoted into HR does the same in reverse. The capacity someone acted in is a fact
   * about the moment of acting, so it is stored at that moment.
   */
  static readonly GRANT_ACTIONS: Record<GrantCapacity, string> = {
    Manager: 'Comp-Off Granted (Manager)',
    HR: 'Comp-Off Granted (HR)',
  };

  /**
   * The capacity encoded in an audit action, or null when the entry does not carry one.
   *
   * Null is the honest answer for rows written before capacity was recorded — they say only
   * 'Comp-Off Granted'. A screen showing those should say the capacity is inferred rather than
   * present a guess as recorded fact.
   */
  static grantCapacityFromAction(action?: string): GrantCapacity | null {
    const value = String(action || '');
    if (value === this.GRANT_ACTIONS.Manager) return 'Manager';
    if (value === this.GRANT_ACTIONS.HR) return 'HR';
    return null;
  }

  /** Whether an audit action is any kind of direct grant, including legacy rows. */
  static isGrantAction(action?: string): boolean {
    return String(action || '').startsWith('Comp-Off Granted');
  }

  /**
   * HR or a manager credits Comp-Off directly, without a claim.
   *
   * Lands APPROVED because the granter is the approver — there is nobody left to ask.
   *
   * `capacity` records which authority was used, and is deliberately supplied by the caller
   * rather than worked out here: the service cannot see the caller's role, and guessing it from
   * the directory is exactly the fragile derivation the note on GRANT_ACTIONS rejects. Omitting
   * it writes the legacy unqualified action, which reads back as "capacity not recorded".
   */
  static async grantCompOff(
    employeeEmail: string,
    workedDate: string,
    isHoliday: boolean,
    daysGranted: number,
    grantedBy: string,
    reason: string,
    capacity?: GrantCapacity
  ): Promise<CompOffRecord> {
    const record = await this.create({
      employeeEmail,
      workedDate,
      isHoliday,
      daysGranted,
      reason,
      grantedBy,
      expiryDate: this.expiryFor(workedDate),
      status: 'APPROVED',
      decidedBy: grantedBy,
      decidedAt: new Date().toISOString(),
    });

    LeaveAuditService.logAction(
      record.id,
      capacity ? this.GRANT_ACTIONS[capacity] : 'Comp-Off Granted',
      grantedBy,
      '0',
      `${daysGranted} day(s)`,
      `Worked on ${workedDate} (${isHoliday ? 'Holiday' : 'Weekend'}). ${reason}`,
      employeeEmail
    );
    return record;
  }

  /**
   * An employee claims Comp-Off for a day they worked.
   *
   * Lands as PENDING and credits nothing: getTotalEarned counts only APPROVED, so an
   * application cannot inflate a balance before anyone has agreed to it. The row goes to
   * the server, which is what puts it in front of the manager and HR.
   */
  static async applyForCompOff(
    employeeEmail: string,
    workedDate: string,
    isHoliday: boolean,
    daysRequested: number,
    reason: string
  ): Promise<CompOffRecord> {
    const record = await this.create({
      employeeEmail,
      workedDate,
      isHoliday,
      daysGranted: daysRequested,
      reason,
      grantedBy: employeeEmail,
      expiryDate: this.expiryFor(workedDate),
    });

    LeaveAuditService.logAction(
      record.id,
      'Comp-Off Requested',
      employeeEmail,
      '0',
      `${daysRequested} day(s)`,
      `Worked on ${workedDate} (${isHoliday ? 'Holiday' : 'Weekend'}). ${reason}`,
      employeeEmail
    );
    return record;
  }

  /**
   * A manager or HR decides a pending application.
   *
   * Approving is the only thing that moves the balance, so the cap is re-checked here as
   * well as at apply time — several applications can sit pending at once, and approving them
   * all could otherwise breach the ceiling together.
   */
  static async decideCompOff(
    id: string,
    approve: boolean,
    decidedBy: string,
    note?: string
  ): Promise<{ ok: boolean; error?: string; record?: CompOffRecord }> {
    const record = this.getRecords().find(r => r.id === id);
    if (!record) return { ok: false, error: 'That Comp-Off request no longer exists.' };
    if (record.status !== 'PENDING') {
      return { ok: false, error: 'This request has already been decided.' };
    }

    // No cap check on approval. There is no maximum balance to breach — the configured number
    // is how long a day stays usable, and that is enforced by expiry in getTotalEarned.

    const decidedAt = new Date().toISOString();
    const status: CompOffRecord['status'] = approve ? 'APPROVED' : 'REJECTED';
    const updated = await this.persistUpdate(record, {
      status,
      decidedBy,
      decidedAt,
      decisionReason: note?.trim() || undefined,
    });

    LeaveAuditService.logAction(
      id,
      approve ? 'Comp-Off Approved' : 'Comp-Off Rejected',
      decidedBy,
      'PENDING',
      status,
      note?.trim() || `${record.daysGranted} day(s) for ${record.workedDate}`,
      record.employeeEmail
    );
    return { ok: true, record: updated };
  }

  /**
   * Send a patch to the server and mirror it into the cache.
   *
   * The cache is updated whether or not the call succeeded, for the same reason
   * LeaveService does it: leaving it stale would make the change appear to vanish on the
   * next read, and the cache is re-seeded from the server by refresh() anyway.
   */
  private static async persistUpdate(
    record: CompOffRecord,
    patch: Partial<CompOffRecord>
  ): Promise<CompOffRecord> {
    let updated: CompOffRecord | null = null;
    try {
      const result = await getClient().graphql({
        query: updateCompOffMutation,
        variables: { input: { id: record.id, ...patch } },
        ...getAuthMode(),
      } as any);
      const raw = getData(result)?.updateCompOff;
      if (raw) updated = normalize(raw);
    } catch (error) {
      console.warn('[CompOff] updateCompOff failed, updating cache only:', error);
    }

    const merged = updated || normalize({ ...record, ...patch });
    this.cacheUpsert(merged);
    notifyChanged();
    return merged;
  }

  /** Applications still awaiting a decision. */
  static pendingRequests(): CompOffRecord[] {
    return this.getRecords().filter(r => r.status === 'PENDING');
  }

  /**
   * How many days a Comp-Off day stays usable for, counted from the date worked.
   *
   * Falls back to the old `compOffMaxDays` setting so a browser holding settings saved under
   * that name keeps HR's configured number instead of silently reverting to the 45 default.
   */
  static getValidityDays(): number {
    const settings = LeavePolicyService.getSettings();
    const configured = settings.compOffValidityDays ?? settings.compOffMaxDays;
    return Number.isFinite(configured) && (configured as number) > 0 ? (configured as number) : 45;
  }

  /**
   * The last date a Comp-Off day earned on `workedDate` can be taken.
   *
   * workedDate + validity window. Computed in UTC from the date parts so it cannot drift by a
   * day across timezones — an expiry that moves depending on where the employee is standing
   * would be indefensible.
   */
  static expiryDateFor(workedDate: string, validityDays = this.getValidityDays()): string {
    const [y, m, d] = (workedDate || '').split('-').map(Number);
    if (!y || !m || !d) return '';
    const due = new Date(Date.UTC(y, m - 1, d));
    due.setUTCDate(due.getUTCDate() + validityDays);
    return due.toISOString().slice(0, 10);
  }

  /**
   * Whether this record's usable window has closed as of `asOf` (today by default).
   *
   * Derived from workedDate every time; the stored `expiryDate` is deliberately NOT trusted.
   * Two reasons, and the first was a live bug:
   *
   *  1. Records created before the window rule existed carry a year-end expiryDate, so a day
   *     worked on 1 August read as usable until 31 December instead of 15 September.
   *  2. The window is a live HR setting. If it were read off the record, changing 45 to 30
   *     would leave every existing Comp-Off on the old window — the setting would only ever
   *     apply to days earned after it was changed, which is not what "the limit is set by HR"
   *     means.
   *
   * The stored field is kept as a record of what the window was when the claim was made.
   */
  static isExpired(record: CompOffRecord, asOf = new Date()): boolean {
    const expiry = this.expiryDateFor(record.workedDate);
    if (!expiry) return false;
    // Usable *through* the expiry date itself, so compare against the day, not the instant.
    return this.todayIso(asOf) > expiry;
  }

  /**
   * Approved, unexpired days for the year — what the employee can actually take right now.
   *
   * Replaces `wouldExceedMax`. That check refused a claim once the accumulated balance passed
   * the configured number, on the reading that it was a maximum balance. It is a validity
   * window, so applying it as a ceiling was enforcing a rule that does not exist — an employee
   * who legitimately worked more weekends than the window's length in days would have had valid
   * claims rejected. There is no cap on how much Comp-Off can be earned; days lapse instead.
   */
  static getUsableDays(employeeEmail: string, year: number): number {
    return this.getTotalEarned(employeeEmail, year);
  }

  static async updateCompOff(
    id: string,
    patch: Partial<Pick<CompOffRecord, 'workedDate' | 'isHoliday' | 'daysGranted' | 'reason' | 'status'>>,
    updatedBy: string
  ): Promise<CompOffRecord | null> {
    const before = this.getRecords().find(r => r.id === id);
    if (!before) return null;

    const updated = await this.persistUpdate(before, patch);

    LeaveAuditService.logAction(
      id,
      'Comp-Off Updated',
      updatedBy,
      `${before.daysGranted} day(s) on ${before.workedDate}`,
      `${updated.daysGranted} day(s) on ${updated.workedDate}`,
      patch.reason ?? updated.reason,
      updated.employeeEmail
    );
    return updated;
  }

  static async deleteCompOff(id: string, removedBy: string, reason?: string): Promise<boolean> {
    const all = this.getRecords();
    const record = all.find(r => r.id === id);
    if (!record) return false;

    try {
      await getClient().graphql({
        query: deleteCompOffMutation,
        variables: { id },
        ...getAuthMode(),
      } as any);
    } catch (error) {
      // Unlike a leave deletion there is no client-side hide list to fall back on, so the
      // row will reappear on the next refresh. Surfacing that honestly beats pretending.
      console.warn('[CompOff] deleteCompOff failed on the server:', error);
      return false;
    }

    this.writeCache(all.filter(r => r.id !== id));

    LeaveAuditService.logAction(
      id,
      'Comp-Off Removed',
      removedBy,
      `${record.daysGranted} day(s) on ${record.workedDate}`,
      '0',
      reason || 'Comp-Off entry removed',
      record.employeeEmail
    );

    notifyChanged();
    return true;
  }
}
